import { useEffect, useState } from "react";
import { Loader2, Send, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import { useAuth } from "@/lib/authContext";
import { emailApi, ClientEmail } from "@/lib/api";
import { EmailAttachmentPicker, useEmailAttachments } from "@/components/custom/email/EmailAttachmentPicker";

interface Props {
  email: ClientEmail | null;
  accountId: string;
  open: boolean;
  onClose: () => void;
  onSent?: () => void;
  /** Reply-all: server/Graph derives sender + all original recipients; To/Cc inputs are disabled. */
  replyAll?: boolean;
}

/** Parse a comma/semicolon-separated address string into Graph recipients. */
function parseRecipients(input: string): { name: string; address: string }[] {
  return input
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((address) => ({ name: "", address }));
}

export function EmailReplyModal({ email, accountId, open, onClose, onSent, replyAll }: Props) {
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const { user } = useAuth();

  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  // ENG-113: same picker and 2.5 MB cap as Compose.
  const attach = useEmailAttachments();

  // Reset the form whenever a new email is opened for reply. Signature is added
  // automatically server-side (server/lib/emailSignature.ts) — the compose box stays plain.
  useEffect(() => {
    if (!email) return;
    const sender = email.from?.emailAddress?.address ?? "";
    setTo(sender);
    setCc("");
    setSubject(email.subject?.toLowerCase().startsWith("re:") ? email.subject : `Re: ${email.subject ?? ""}`);
    setBody("");
    attach.clear();
  }, [email, user?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAiDraft = async () => {
    if (!email) return;
    if (body.trim() && !(await askConfirm({
      title: "Replace your draft?",
      subject: "draft",
      body: "will be replaced with an AI-generated reply.",
      lines: ["What you have written so far is not recoverable."],
      confirmLabel: "Replace draft",
      destructive: true,
    }))) {
      return;
    }
    setDrafting(true);
    try {
      const { draft } = await emailApi.aiDraftReply(email.mailboxAddress, email.id, user?.name);
      setBody(draft.trim());
    } catch (err: any) {
      toast({ title: "Could not generate AI draft", description: err.message, variant: "destructive" });
    } finally {
      setDrafting(false);
    }
  };

  const handleSend = async () => {
    if (!email) return;
    const recipients = parseRecipients(to);
    if (!replyAll && !recipients.length) {
      toast({ title: "At least one recipient is required", variant: "destructive" });
      return;
    }
    if (!body.trim()) {
      toast({ title: "Message body is required", variant: "destructive" });
      return;
    }
    if (attach.overCap) {
      toast({ title: "Attachments exceed the 2.5 MB total limit", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await emailApi.replyToEmail({
        mailbox: email.mailboxAddress,
        message_id: email.id,
        account_id: accountId,
        to: recipients,
        cc: cc.trim() ? parseRecipients(cc) : undefined,
        subject,
        body,
        reply_all: replyAll,
        attachments: attach.payload(),
      });
      toast({ title: "Reply sent", description: `From ${email.mailboxAddress}` });
      onSent?.();
      onClose();
    } catch (err: any) {
      toast({ title: "Could not send reply", description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{replyAll ? "Reply all" : "Reply"}</DialogTitle>
        </DialogHeader>

        {email && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">From (sending as)</Label>
              <Input value={email.mailboxAddress} readOnly className="bg-muted/50 cursor-not-allowed" />
            </div>
            {replyAll ? (
              <p className="text-xs text-muted-foreground">Replying to sender and all recipients</p>
            ) : (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">To</Label>
                  <Input
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="recipient@example.com"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cc <span className="text-muted-foreground">(optional)</span></Label>
                  <Input
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="comma-separated"
                  />
                </div>
              </>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Message</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs border-violet-300 text-violet-600 hover:bg-violet-50 hover:text-violet-700"
                  onClick={handleAiDraft}
                  disabled={sending || drafting}
                >
                  {drafting ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="mr-1 h-3 w-3" />
                  )}
                  AI draft
                </Button>
              </div>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="min-h-[180px] text-sm"
              />
              <p className="text-[11px] text-muted-foreground">
                The client sees this from {email.mailboxAddress}. Your signature is added automatically.
              </p>
            </div>
            <EmailAttachmentPicker state={attach} disabled={sending} />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending || attach.overCap}>
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send reply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
